/**
 * Question Service
 * Handles question-related API operations
 */

import { httpClient } from '../lib/httpClient';
import type { 
  PublishQuestionsRequest, 
  PublishQuestionsResponse,
  DownloadQuestionPackageRequest,
  DownloadQuestionPackageResponse,
  DeleteQuestionsRequest,
  DeleteQuestionsResponse,
  QuestionsPresignedRequest,
  QuestionsPresignedResponse
} from '../interfaces/apiInterfaces/questions';

/**
 * Publish one or more questions for a job
 * @param jobId - GUID referencing an existing job
 * @param questionIds - Array of 12-digit question identifiers to publish
 * @returns Promise that resolves with publish results including published_count, failed_count, and details
 */
export async function publishJobQuestions(
  jobId: string,
  questionIds: string[]
): Promise<PublishQuestionsResponse> {
  try {
    if (questionIds.length > 10) {
      const batches = [];
      for (let i = 0; i < questionIds.length; i += 10) {
        batches.push(questionIds.slice(i, i + 10));
      }

      const responses = [];
      for (const batch of batches) {
        const requestBody: PublishQuestionsRequest = {
          job_id: jobId,
          question_ids: batch,
        };


        const response = await httpClient.post<PublishQuestionsResponse>(
          '/v1/questions/publish',
          requestBody
        );
        responses.push(response);
      }

     const result = responses.reduce<PublishQuestionsResponse>(
  (combined, response) => ({
    job_id: jobId,
    published_count: combined.published_count + response.data.published_count,
    failed_count: combined.failed_count + response.data.failed_count,
    details: combined.details.concat(response.data.details),
  }),
  {
    job_id: jobId,
    published_count: 0,
    failed_count: 0,
    details: [],
  }
);
return result;
    }

    const requestBody: PublishQuestionsRequest = {
      job_id: jobId,
      question_ids: questionIds,
    };

    const response = await httpClient.post<PublishQuestionsResponse>(
      '/v1/questions/publish',
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error(`Error publishing questions for job ${jobId}:`, error);
    throw error;
  }
}

/**
 * Download a ZIP package for one or more published questions
 * @param questionIds - Array of 12-digit question identifiers to download
 * @param jobId - Optional job ID when downloading in job context (from URL or state)
 * @returns Promise that resolves with download_url and s3_key
 * @throws Error if question IDs are invalid (not 12-digit numbers)
 */
export async function downloadQuestionPackage(
  questionIds: string[],
  jobId?: string | null
): Promise<DownloadQuestionPackageResponse> {
  try {
    // Validate that all IDs are 12-digit strings
    questionIds.forEach(id => {
      if (id.length !== 12 || !/^\d{12}$/.test(id)) {
        throw new Error(`Invalid question ID: ${id}. Must be a 12-digit string.`);
      }
    });

    const requestBody: DownloadQuestionPackageRequest = {
      question_ids: questionIds,
      ...(jobId != null && jobId !== '' && { job_id: jobId }),
    };

    const response = await httpClient.post<DownloadQuestionPackageResponse>(
      '/v1/questions/download-package',
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error(`Error downloading question package:`, error);
    throw error;
  }
}

/**
 * Delete one or more questions by their IDs
 * @param questionIds - Array of question identifiers to delete
 * @returns Promise that resolves with delete results including deleted_count, failed_count, and details
 */
export async function deleteQuestions(
  questionIds: string[]
): Promise<DeleteQuestionsResponse> {
  try {
    const requestBody: DeleteQuestionsRequest = {
      questions: questionIds,
    };

    const response = await httpClient.post<DeleteQuestionsResponse>(
      '/v1/questions/delete',
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error(`Error deleting questions:`, error);
    throw error;
  }
}

/**
 * Get presigned URLs for question package files
 * @param objectKeys - Array of S3 object keys to get presigned URLs for
 * @returns Promise that resolves with an array of { object_key, presigned_url } items
 */
export async function getQuestionPresignedUrls(
  objectKeys: string[]
): Promise<QuestionsPresignedResponse> {
  try {
    const requestBody: QuestionsPresignedRequest = {
      object_keys: objectKeys,
    };

    const response = await httpClient.post<QuestionsPresignedResponse>(
      '/v1/questions/presigned',
      requestBody
    );
    return response.data;
  } catch (error) {
    console.error('Error getting presigned URLs:', error);
    throw error;
  }
}